import React, { useEffect, useState } from "react";
import accountsFlowApi from "../../services/accountsFlowService";
import { formatINR, todayInput } from "./accountUser";

const paise = (value) => Math.round((Number(value) || 0) * 100);

/* One receipt split across the customer's open invoices. Anything left over stays as advance. */
export default function ReceiptSplit({ ledgerId, receiptAmount, allocations, onChange, onIssue, onAdvance, disabled, presetInvoice }) {
  const [customer, setCustomer] = useState(null);
  const [rows, setRows] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [advanceAvailable, setAdvanceAvailable] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!ledgerId) {
      setCustomer(null);
      setRows([]);
      setRemoved([]);
      setAdvanceAvailable(0);
      setLoadError("");
      onChange({});
      onIssue("");
      if (onAdvance) onAdvance(0);
      return undefined;
    }
    let live = true;
    setLoading(true);
    setLoadError("");
    accountsFlowApi.openInvoices(ledgerId)
      .then((result) => {
        if (!live) return;
        const data = result?.data || {};
        const list = data.invoices || [];
        const available = Number(data.advanceAvailable) || 0;
        setCustomer(data.customer || null);
        setRows(list);
        setRemoved([]);
        setAdvanceAvailable(available);
        if (onAdvance) onAdvance(available);
        const seed = {};
        if (presetInvoice) {
          const hit = list.find((row) => row.invoiceNr === presetInvoice);
          const cap = paise(receiptAmount) + paise(available);
          if (hit && hit.remaining > 0 && cap > 0) {
            const take = Math.min(paise(hit.remaining), cap);
            if (take > 0) seed[hit.invoiceNr] = (take / 100).toFixed(2);
          }
        }
        onChange(seed);
      })
      .catch((err) => {
        if (!live) return;
        setCustomer(null);
        setRows([]);
        setAdvanceAvailable(0);
        if (onAdvance) onAdvance(0);
        setLoadError(err?.message || "Could not load open invoices");
        onChange({});
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // Reload only when the customer ledger (or the invoice this page was opened from) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerId, presetInvoice]);

  useEffect(() => {
    const over = rows.find((row) => paise(allocations[row.invoiceNr]) > paise(row.remaining));
    onIssue(over ? `${over.invoiceNr} has only ${formatINR(over.remaining)} still open` : "");
  }, [rows, allocations, onIssue]);

  const visible = rows.filter((row) => !removed.includes(row.invoiceNr));
  const allocated = visible.reduce((sum, row) => sum + paise(allocations[row.invoiceNr]), 0);
  const receipt = paise(receiptAmount);
  const available = paise(advanceAvailable);
  const pool = receipt + available;
  const advanceUsed = Math.max(0, allocated - receipt);
  const advanceLeft = Math.max(0, pool - allocated);
  const today = todayInput();

  const setAmount = (invoiceNr, value) => {
    const next = { ...allocations };
    if (!String(value).trim()) delete next[invoiceNr];
    else next[invoiceNr] = value;
    onChange(next);
  };

  const removeInvoice = (invoiceNr) => {
    setRemoved((current) => (current.includes(invoiceNr) ? current : [...current, invoiceNr]));
    const next = { ...allocations };
    delete next[invoiceNr];
    onChange(next);
  };

  const fillByDue = () => {
    let left = pool;
    const next = {};
    visible.forEach((row) => {
      if (left <= 0) return;
      const take = Math.min(paise(row.remaining), left);
      if (take > 0) {
        next[row.invoiceNr] = (take / 100).toFixed(2);
        left -= take;
      }
    });
    onChange(next);
  };

  return (
    <div className="receipt-split">
      <div className="receipt-split-head">
        <div>
          <strong>Allocate to invoices</strong>
          <span>
            {customer
              ? `${customer.name || customer.code}${customer.creditDays ? ` · ${customer.creditDays} credit days` : ""}. Invoices closest to the due date are listed first.`
              : "Select the customer ledger. Enter what they paid now, then apply it and any earlier advance to the invoices below."}
          </span>
        </div>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={fillByDue} disabled={disabled || !visible.length || pool <= 0}>
          Apply to due invoices
        </button>
      </div>
      {customer && available > 0 && (
        <p className="receipt-advance-note">
          Advance available {formatINR(available / 100)}. Apply it to an invoice below. Enter 0 as the new amount if the customer is not paying anything more.
        </p>
      )}
      {loading && <p className="accounts-hint">Loading open invoices…</p>}
      {loadError && <div className="accounts-error">{loadError}</div>}
      {!loading && ledgerId && !customer && !loadError && (
        <p className="accounts-hint">This ledger is not a customer. The receipt can still be saved, and the whole amount stays unallocated.</p>
      )}
      {!loading && customer && rows.length === 0 && (
        <p className="accounts-hint">No open invoices for this customer. New money stays as advance. When the next loading invoice is raised, open a receipt, enter 0, and apply this advance to it.</p>
      )}
      {visible.length > 0 && (
        <div className="accounts-table-wrap">
          <table className="accounts-table receipt-split-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Date</th>
                <th>Due</th>
                <th className="num">Invoice</th>
                <th className="num">Still open</th>
                <th className="num">This receipt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const due = row.dueDate && row.dueDate <= today;
                return (
                  <tr key={row.invoiceNr} className={due ? "is-due" : ""}>
                    <td>
                      <strong>{row.invoiceNr}</strong>
                      {row.pending > 0 ? <div className="accounts-hint">{formatINR(row.pending)} already waiting for approval</div> : null}
                    </td>
                    <td>{row.date || "—"}</td>
                    <td>{row.dueDate || "—"}{due ? <div className="accounts-hint">Due</div> : null}</td>
                    <td className="num">{formatINR(row.amount)}</td>
                    <td className="num">{formatINR(row.remaining)}</td>
                    <td className="num">
                      <input
                        className="form-control"
                        inputMode="decimal"
                        value={allocations[row.invoiceNr] || ""}
                        placeholder="0.00"
                        disabled={disabled}
                        onChange={(event) => setAmount(row.invoiceNr, event.target.value)}
                      />
                    </td>
                    <td>
                      <button type="button" className="receipt-split-remove" title={`Remove ${row.invoiceNr}`} disabled={disabled} onClick={() => removeInvoice(row.invoiceNr)}>
                        <i className="bi bi-trash" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="receipt-split-sum">
        <span>New money {receipt > 0 ? formatINR(receipt / 100) : formatINR(0)}</span>
        <span>Advance available {formatINR(available / 100)}</span>
        <span>Allocated {formatINR(allocated / 100)}</span>
        <span>From advance {formatINR(advanceUsed / 100)}</span>
        <span>Left unallocated {formatINR(advanceLeft / 100)}</span>
      </div>
    </div>
  );
}
